/**
 * Rough daily-budget minimums per currency for ad platforms.
 *
 * These are *hints* shown next to budget inputs so users don't submit values
 * the platform will reject. Platforms change minimums and apply additional
 * objective- or placement-specific floors, so this table is intentionally
 * conservative and not used to block submission — the platform's own
 * validation remains the source of truth.
 *
 * Numbers are in major currency units (e.g. 2500 = ₨2,500, 5 = AED 5).
 */

type MinTable = Record<string, number>;

const TIKTOK_MIN_DAILY: MinTable = {
  USD: 20,
  EUR: 20,
  GBP: 15,
  AED: 70,
  SAR: 70,
  PKR: 2500,
  INR: 1600,
  IDR: 280000,
  TRY: 600,
  EGP: 600,
  MYR: 90,
  THB: 700,
  PHP: 1100,
  VND: 480000,
};

const META_MIN_DAILY: MinTable = {
  USD: 1,
  EUR: 1,
  GBP: 1,
  AED: 4,
  SAR: 4,
  PKR: 200,
  INR: 80,
  IDR: 14000,
  TRY: 30,
  EGP: 30,
  MYR: 5,
  THB: 30,
  PHP: 50,
  VND: 25000,
};

export type AdPlatform = 'tiktok' | 'meta';

export function minDailyBudget(platform: AdPlatform, currency: string | null | undefined): number | null {
  if (!currency) return null;
  const table = platform === 'tiktok' ? TIKTOK_MIN_DAILY : META_MIN_DAILY;
  return table[currency.toUpperCase()] ?? null;
}

const PLATFORM_NAME: Record<AdPlatform | 'google', string> = { meta: 'Facebook', tiktok: 'TikTok', google: 'Google Ads' };

/**
 * Budget/bid field hint in the platform's own billing currency. Amounts are
 * always sent to the platform in that currency — never converted.
 */
export function formatBudgetHint(platform: AdPlatform | 'google', currency: string | null | undefined): string {
  const name = PLATFORM_NAME[platform];
  if (!currency) return `We don't know this ${name} account's currency yet — refresh the account before setting a budget.`;
  const base = `This ${name} account bills in ${currency} — enter the amount in ${currency}. ${name} sets this currency.`;
  const min = platform === 'google' ? null : minDailyBudget(platform, currency);
  return min === null ? base : `${base} Minimum is about ${currency} ${min.toLocaleString()} / day.`;
}
